<script>

import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'

export default {
	components: {
        VueApexCharts
    },
  props: {
    title: {
      type: String,
      default: ""
    },
    type: {
      type: String,
      default: "line"
    },
    series: {
      type: Array,
      default: function () {
        return []
      }
    },
    collection: "",
    show_last_modification: {
      type: Boolean,
      default: false
    },
    start: {
      type: Number,
      default: 0
    },
    end: {
      type: Number,
      default: 0
    },
    range: {
			type: Boolean,
			default: false
    },
    logarithmic: {
      type: Boolean,
      default: false
    },
  },
  data: function () {
    return {
      name: window.name,
			appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      refresh: 60000,
      downloadedSeries: [],
      downloadedLabels: [],
      colors: [],
      months: ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      chartHeight: 295,
      chartOptions: {
        chart: {
          // A number, never 'auto'. vue-apexcharts re-applies this whole
          // options object on every change and does not re-inject the height
          // prop it used at init, so an 'auto' here takes over the moment the
          // options are replaced: ApexCharts then sizes the chart against its
          // parent and the drawing either collapses to nothing or grows past
          // the bottom of the card and paints over the widgets below it.
          height: 295,
        },
        stroke: {
          width: [1, 2, 2, 2, 2]
        },
        // dataLabels: {
        //   enabled: true,
        //   enabledOnSeries: [1]
        // },
        xaxis: {
          type: 'datetime'
        },
        tooltip: {
          x: {
            format: "dd / MM / yyyy"
          }
        },
        colors: [],
        labels: [],
        series: []
      },
    }
  },
  created: async function () {
    await this.load()
  },
  methods: {
    // The dashboard charts span the whole history, so the range defaults to the
    // epoch - but only when the widget did not already ask for a window of its
    // own, otherwise a 30-day chart would silently become an all-time one.
    buildURL: function (source) {
      if (/[?&](start|end|minutes)=/.test(source)) return source
      return source + '&start=0'
    },
    labelFor: function (entry) {
      if (entry.day !== undefined) {
        return entry.day + ' ' + this.months[entry.month] + ' ' + entry.year.toString()
      }
      return '01 ' + this.months[entry.month] + ' ' + entry.year.toString()
    },
    fetchSerie: function (serie) {
      const that = this
      const baseURL = that.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
      if (serie.source === undefined) {
        console.error('Mixed chart error - source not defined for serie', serie.name)
        return Promise.resolve([])
      }
      return axios
        .get(baseURL + that.buildURL(serie.source))
        .then(function (response) {
          if (response.status !== 200) return []
          if (response.data.data === undefined || response.data.updated === undefined) {
            console.error('Mixed chart error - data not defined in response for', serie.name)
            return []
          }
          that.lastUpdated = new Date(response.data.updated).toLocaleString()
          return response.data.data
        })
        .catch(function (error) {
          console.error('Mixed chart error loading', serie.name, error.response)
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            localStorage.removeItem('idToken')
            that.$router.push({ name: 'Home' })
          }
          return []
        })
    },
    load: async function () {
      const that = this
      // Every serie is requested at once and the chart is assembled only when
      // all of them are in. The previous version started the remaining series
      // on a 2.5 s timer and hoped the first one had finished by then, which
      // fell apart whenever a query ran long.
      const responses = await Promise.all(that.series.map(function (serie) {
        return that.fetchSerie(serie)
      }))

      let labelIndex = that.series.findIndex(function (serie) { return serie.labelsSource })
      if (labelIndex < 0) labelIndex = 0
      const labels = responses[labelIndex].map(function (entry) { return that.labelFor(entry) })

      // Position by label, not by arrival order: a serie that has no incidents
      // in a given month returns fewer rows, and pushing its values in sequence
      // used to shift every later point onto the wrong month.
      const positions = {}
      labels.forEach(function (label, index) { positions[label] = index })

      const series = []
      const colors = []
      that.series.forEach(function (serie, index) {
        // On a logarithmic axis a month with no incidents is a gap, not a
        // point: log(0) has no place on the scale, and feeding zeros in drags
        // every series down onto the baseline.
        const empty = that.logarithmic ? null : 0
        const data = new Array(labels.length).fill(empty)
        responses[index].forEach(function (entry) {
          const at = positions[that.labelFor(entry)]
          if (at !== undefined) data[at] = entry.count > 0 ? entry.count : empty
        })
        series.push({ name: serie.name, type: serie.type, data: data })
        colors.push(serie.color || '#d0d0d0')
      })

      // A single month of bulk ingestion can be twenty-five times a normal one,
      // which on a linear axis flattens the other eighty into the baseline.
      const axis = that.logarithmic
        ? {
          logarithmic: true,
          tickAmount: 4,
          labels: {
            formatter: function (value) {
              return value === null ? '' : Math.round(value).toLocaleString()
            }
          }
        }
        : {}

      // Assigning new objects (rather than mutating the existing arrays) is what
      // makes ApexCharts pick the change up, and it keeps colours and names
      // aligned with the declared order of the series.
      that.colors = colors
      that.downloadedLabels = labels
      const updated = { labels: labels, colors: colors }
      // Only touch the axis when the widget asked for it, so the charts that
      // keep a linear scale are left exactly as they were.
      if (that.logarithmic) updated.yaxis = axis
      that.chartOptions = Object.assign({}, that.chartOptions, updated)
      that.downloadedSeries = series
      that.isBusy = false
    }
  }
}
</script>

<template>
  <div>
    <div class="mixed-chart">
      <VueApexCharts
        ref="vchart"
        :type="type"
        :height="chartHeight"
        :options="chartOptions"
        :series="downloadedSeries"
      />
    </div>
  </div>
</template>

<style scoped>
  /* Last line of defence: whatever the data does to the scale, the drawing is
     clipped to the widget instead of spilling over its neighbours. */
  .mixed-chart {
    overflow: hidden;
    max-width: 100%;
    text-align: left;
  }

  .apexcharts-legend {
      padding-right: 80px;
      line-height: 5px;
      font-size: 8px;
  }
</style>
